import {
  ArrowRight,
  Check,
  Circle,
  Flag,
  HardDrive,
  Hand,
  Layers,
  Loader2,
  Minus,
  Play,
  RotateCcw,
  ScrollText,
  ShieldCheck,
  Sparkles,
  UserCheck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { memo, useCallback, useEffect, useReducer, useRef } from 'react';
import type { Dispatch, RefObject } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  CANARY,
  PLAYBOOKS,
  PROOFS,
  SERVERS,
  afterConfirmation,
  playbookById,
  stoppedAtConfirmation,
  untilConfirmation,
} from '@/modules/marketing/demo/rollout-demo-script';
import type {
  CheckState,
  DemoEvent,
  DemoPlaybook,
  LogStage,
  PlaybookId,
  Proof,
  ServerId,
  ServerState,
  Stage,
  StageState,
  Tone,
} from '@/modules/marketing/demo/rollout-demo-script';
import { TRIAL } from '@/modules/marketing/pricing-data';

/**
 * The landing page's live demo: a playbook run on four simulated servers, played in the browser.
 *
 * ## Why a run, and why this one
 *
 * The fear a visitor arrives with is "a tool that changes all my servers at once". A feature list
 * cannot answer that; watching one change be checked everywhere, tried on one server and held until
 * *they* say go can. So the demo is a run with the visitor in the confirming seat: the only way to
 * reach the rest of the fleet is to press the button a real run waits for.
 *
 * ## It is a simulation, and says so
 *
 * Labelled as one in three places, with nothing sent anywhere. What happens and in what order is
 * the product's own behaviour, and what the playbooks report is what their scripts report; see
 * rollout-demo-script.ts, which holds every word the console prints.
 *
 * ## Built for the pre-render
 *
 * The first render is the idle state, identical on the server and in the browser. Timers start
 * only from a click, and the page never scrolls unless a result would otherwise appear out of sight
 * of somebody who is still looking at the demo.
 */

type Phase = 'idle' | 'running' | 'waiting' | 'done' | 'stopped';

interface Line {
  id: number;
  stage: LogStage;
  text: string;
  tone: Tone;
}

type NewLine = Omit<Line, 'id'>;

interface ServerView {
  state: ServerState;
  disk: number;
}

type Change = Exclude<DemoEvent, { kind: 'log' } | { kind: 'pause' }>;

interface DemoState {
  phase: Phase;
  playbook: PlaybookId;
  servers: Record<ServerId, ServerView>;
  stages: Record<Stage, StageState>;
  checks: Record<Stage, CheckState[]>;
  /** Share of the fleet finished, in percent. */
  progress: number;
  lines: Line[];
  /** The line being typed, and how much of it shows. */
  typing: { line: NewLine; shown: number } | null;
  proofs: Proof[];
  /** For screen readers: the console itself is not announced, since it types. */
  announcement: string;
}

type Action =
  | { type: 'select'; playbook: PlaybookId }
  | { type: 'start' }
  | { type: 'phase'; phase: Phase }
  | { type: 'typing'; line: NewLine; shown: number }
  | { type: 'line'; line: NewLine }
  | { type: 'change'; change: Change };

function initialState(playbook: PlaybookId): DemoState {
  const servers = {} as Record<ServerId, ServerView>;

  for (const server of SERVERS) {
    servers[server.id] = { state: 'ready', disk: server.disk };
  }

  return {
    phase: 'idle',
    playbook,
    servers,
    stages: { preflight: 'pending', canary: 'pending', rollout: 'pending' },
    checks: {
      preflight: ['pending', 'pending', 'pending'],
      canary: ['pending', 'pending', 'pending'],
      rollout: ['pending', 'pending', 'pending'],
    },
    progress: 0,
    lines: [],
    typing: null,
    proofs: [],
    announcement: '',
  };
}

function reduce(state: DemoState, action: Action): DemoState {
  switch (action.type) {
    case 'select':
      return initialState(action.playbook);
    case 'start':
      return { ...initialState(state.playbook), phase: 'running' };
    case 'phase':
      return { ...state, phase: action.phase };
    case 'typing':
      return { ...state, typing: { line: action.line, shown: action.shown } };
    case 'line':
      return { ...state, typing: null, lines: [...state.lines, { ...action.line, id: state.lines.length + 1 }] };
    case 'change':
      return applyChange(state, action.change);
  }
}

function applyChange(state: DemoState, change: Change): DemoState {
  switch (change.kind) {
    case 'servers': {
      const servers = { ...state.servers };

      for (const id of change.ids) {
        servers[id] = { ...servers[id], state: change.state };
      }

      return { ...state, servers };
    }
    case 'disk':
      return { ...state, servers: { ...state.servers, [change.id]: { ...state.servers[change.id], disk: change.disk } } };
    case 'stage':
      return { ...state, stages: { ...state.stages, [change.stage]: change.state } };
    case 'check': {
      const checks = [...state.checks[change.stage]];
      checks[change.index] = change.state;

      return { ...state, checks: { ...state.checks, [change.stage]: checks } };
    }
    case 'progress':
      return { ...state, progress: change.value };
    case 'proof':
      return state.proofs.includes(change.proof) ? state : { ...state, proofs: [...state.proofs, change.proof] };
    case 'announce':
      return { ...state, announcement: change.text };
  }
}

// ── Playing the script ──────────────────────────────────────────────────────

/** One frame, near enough. Typing advances a few characters per frame rather than one per timer. */
const FRAME_MS = 16;

/** How long each kind of change holds before the next, so a visitor sees it happen. */
const HOLD_MS: Record<Change['kind'], number> = {
  servers: 90,
  disk: 0,
  stage: 160,
  check: 90,
  progress: 0,
  proof: 0,
  announce: 0,
};

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Stopped', 'AbortError'));

      return;
    }

    const stop = () => {
      window.clearTimeout(timer);
      reject(new DOMException('Stopped', 'AbortError'));
    };
    const timer = window.setTimeout(() => {
      signal.removeEventListener('abort', stop);
      resolve();
    }, ms);

    signal.addEventListener('abort', stop, { once: true });
  });
}

/**
 * Plays events in order. A line is typed a few characters a frame; the preflight's lines faster,
 * because in a real run the four servers are checked at once. With reduced motion a line appears
 * whole and only the pauses between lines remain.
 */
async function play(events: DemoEvent[], dispatch: Dispatch<Action>, signal: AbortSignal, typed: boolean): Promise<void> {
  for (const event of events) {
    if (event.kind === 'pause') {
      await sleep(event.ms, signal);
      continue;
    }

    if (event.kind !== 'log') {
      dispatch({ type: 'change', change: event });

      if (HOLD_MS[event.kind] > 0) {
        await sleep(HOLD_MS[event.kind], signal);
      }
      continue;
    }

    const quick = event.stage === 'preflight';
    const line: NewLine = { stage: event.stage, text: event.text, tone: event.tone ?? 'plain' };

    await sleep(event.after ?? (quick ? 45 : 120), signal);

    if (typed) {
      const step = quick ? 10 : 5;

      for (let shown = step; shown < line.text.length; shown += step) {
        dispatch({ type: 'typing', line, shown });
        await sleep(FRAME_MS, signal);
      }
    }

    dispatch({ type: 'line', line });
  }
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Brings the confirmation or the result into view for somebody still watching the demo.
 *
 * On a phone both appear well below the fold, under the console, and a visitor left looking at a
 * console that says "waiting for you" with no button in sight has been given nothing to press. So
 * while any part of the demo is on screen, the page moves just enough to show the element. A visitor
 * who has scrolled away from the demo altogether is left where they are.
 */
function revealIfWatching(element: HTMLElement | null, demo: HTMLElement | null): void {
  if (element === null || demo === null) {
    return;
  }

  const height = window.innerHeight;
  const rect = element.getBoundingClientRect();
  const area = demo.getBoundingClientRect();

  if ((rect.top >= 0 && rect.bottom <= height) || area.bottom <= 0 || area.top >= height) {
    return;
  }

  element.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
}

// ── The widget ──────────────────────────────────────────────────────────────

export function RolloutDemo() {
  const [state, dispatch] = useReducer(reduce, PLAYBOOKS[0].id, initialState);
  const player = useRef<AbortController | null>(null);
  const picker = useRef<HTMLFieldSetElement>(null);
  const outcome = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const playbook = playbookById(state.playbook);
  const { phase } = state;
  const busy = phase === 'running' || phase === 'waiting';

  // Leaving the page mid-run stops the timers with it.
  useEffect(() => () => player.current?.abort(), []);

  useEffect(() => {
    if (phase === 'waiting' || phase === 'done' || phase === 'stopped') {
      revealIfWatching(outcome.current, panel.current);
    }
  }, [phase]);

  const perform = useCallback((events: DemoEvent[], then: Phase) => {
    player.current?.abort();
    const controller = new AbortController();
    player.current = controller;

    void play(events, dispatch, controller.signal, !prefersReducedMotion())
      .then(() => dispatch({ type: 'phase', phase: then }))
      .catch((error: unknown) => {
        // Stopped on purpose: another run, another playbook, or the page going away.
        if (!isAbort(error)) {
          throw error;
        }
      });
  }, []);

  const start = useCallback(() => {
    dispatch({ type: 'start' });
    perform(untilConfirmation(playbook), 'waiting');
  }, [perform, playbook]);

  const proceed = useCallback(() => {
    dispatch({ type: 'phase', phase: 'running' });
    perform(afterConfirmation(playbook), 'done');
  }, [perform, playbook]);

  const stop = useCallback(() => {
    dispatch({ type: 'phase', phase: 'running' });
    perform(stoppedAtConfirmation(), 'stopped');
  }, [perform]);

  const select = useCallback((id: PlaybookId) => {
    player.current?.abort();
    dispatch({ type: 'select', playbook: id });
  }, []);

  const another = useCallback(() => {
    player.current?.abort();
    dispatch({ type: 'select', playbook: state.playbook });
    picker.current?.querySelector<HTMLInputElement>('input:checked')?.focus();
  }, [state.playbook]);

  return (
    <div>
      {/* ── Heading ─────────────────────────────────────────────────────────── */}
      <div className="mx-auto max-w-2xl text-center">
        <span className="border-primary/30 bg-primary/10 text-primary inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium">
          <Sparkles className="size-3.5" aria-hidden />
          Live Interactive Demo · No registration required
        </span>
        <h2 className="mt-4 text-2xl font-semibold tracking-tight text-balance sm:text-3xl">See Techie Gain in Action</h2>
        <p className="text-muted-foreground mt-3 text-[15px] leading-relaxed text-balance">
          Pick a playbook and run it on four simulated servers. Every server is checked first, one goes
          first, and the rest wait until you say so.
        </p>
      </div>

      {/* ── The run ─────────────────────────────────────────────────────────── */}
      <div className="relative mt-10 sm:mt-12">
        <div aria-hidden className="bg-primary/15 pointer-events-none absolute -inset-x-6 -inset-y-8 rounded-[2rem] blur-3xl" />

        <div ref={panel} className="border-border/60 bg-card relative overflow-hidden rounded-2xl border shadow-2xl">
          <div
            aria-hidden
            className="via-primary/60 absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent to-transparent"
          />

          {/* Where this would be in the product, and what it is here. */}
          <div className="border-border/60 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b px-4 py-3 sm:px-5">
            <p className="flex min-w-0 items-center gap-2 text-sm">
              <span className="bg-success size-2 shrink-0 rounded-full" aria-hidden />
              <span className="font-medium">Techie Gain</span>
              <span className="text-muted-foreground truncate">/ Automation / New run</span>
            </p>
            <p className="text-muted-foreground text-xs">Simulation · nothing connects to a real server</p>
          </div>

          <div className="grid gap-6 p-4 sm:p-5 lg:grid-cols-[340px_minmax(0,1fr)] lg:gap-7">
            {/* ── Configuration ─────────────────────────────────────────────── */}
            <div className="space-y-5">
              <PlaybookPicker anchor={picker} selected={state.playbook} busy={busy} onSelect={select} />
              <ServerGrid servers={state.servers} playbook={playbook} canaryStarted={state.stages.canary !== 'pending'} />

              <div>
                <p className="text-sm font-medium">Rollout</p>
                <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                  Canary first, then batches of 2, stopping at the first failure.
                </p>
              </div>

              <RunButton phase={phase} onStart={start} />
            </div>

            {/* ── What happens ──────────────────────────────────────────────── */}
            <div className="flex min-w-0 flex-col gap-4">
              <Pipeline stages={state.stages} checks={state.checks} progress={state.progress} />
              <Console lines={state.lines} typing={state.typing} phase={phase} playbookName={playbook.name} />

              {phase === 'waiting' && <Checkpoint anchor={outcome} onStop={stop} onContinue={proceed} />}
              {(phase === 'done' || phase === 'stopped') && (
                <Outcome anchor={outcome} stopped={phase === 'stopped'} playbook={playbook} onAnother={another} />
              )}
            </div>
          </div>
        </div>
      </div>

      <ProofStrip proofs={state.proofs} />

      <p className="text-muted-foreground/80 mx-auto mt-6 max-w-2xl text-center text-xs leading-relaxed text-balance">
        A simulation: the servers, package counts and disk figures are examples, and time is compressed.
        The checks, their order and the confirmation before the rest of the fleet are how a real run works.
      </p>

      <p className="sr-only" aria-live="polite">
        {state.announcement}
      </p>
    </div>
  );
}

// ── Configuration ───────────────────────────────────────────────────────────

const PlaybookPicker = memo(function PlaybookPicker({
  anchor,
  selected,
  busy,
  onSelect,
}: {
  anchor: RefObject<HTMLFieldSetElement>;
  selected: PlaybookId;
  busy: boolean;
  onSelect: (id: PlaybookId) => void;
}) {
  return (
    <fieldset ref={anchor} disabled={busy}>
      <legend className="text-sm font-medium">Choose a playbook</legend>
      <div className="mt-2.5 space-y-2">
        {PLAYBOOKS.map((option) => {
          const checked = option.id === selected;

          return (
            <label
              key={option.id}
              className={cn(
                'has-[:focus-visible]:ring-ring flex items-start gap-3 rounded-lg border px-3 py-2 transition-colors has-[:focus-visible]:ring-2',
                checked ? 'border-primary/50 bg-primary/[0.07]' : 'border-border/60',
                busy ? 'cursor-not-allowed' : 'cursor-pointer',
                !checked && (busy ? 'opacity-50' : 'hover:bg-muted/40'),
              )}
            >
              <input
                type="radio"
                name="demo-playbook"
                value={option.id}
                checked={checked}
                onChange={() => onSelect(option.id)}
                className="sr-only"
              />
              <span
                aria-hidden
                className={cn(
                  'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors',
                  checked ? 'border-primary bg-primary' : 'border-muted-foreground/40',
                )}
              >
                {checked && <span className="size-1.5 rounded-full bg-white" />}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium">{option.label}</span>
                <span className="text-muted-foreground block text-xs leading-relaxed">{option.summary}</span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
});

const SERVER_BADGE: Record<ServerState, { label: string; className: string; spin?: boolean }> = {
  ready: { label: 'Ready', className: 'bg-muted/60 text-muted-foreground' },
  checking: { label: 'Checking', className: 'bg-primary/10 text-primary', spin: true },
  waiting: { label: 'Waiting', className: 'bg-muted/60 text-muted-foreground' },
  canary: { label: 'Canary', className: 'bg-warning/15 text-warning', spin: true },
  working: { label: '', className: 'bg-primary/10 text-primary', spin: true },
  success: { label: 'Success', className: 'bg-success/15 text-success' },
  untouched: { label: 'Not touched', className: 'bg-muted/60 text-muted-foreground' },
};

/** The health line: disk use, coloured the way a server-health monitor would see it. */
function diskTone(disk: number): { dot: string; bar: string; label: string } {
  if (disk >= 90) {
    return { dot: 'bg-danger', bar: 'bg-danger', label: 'Disk nearly full' };
  }

  if (disk >= 80) {
    return { dot: 'bg-warning', bar: 'bg-warning', label: 'Disk filling up' };
  }

  return { dot: 'bg-success', bar: 'bg-primary/60', label: 'Healthy' };
}

const ServerGrid = memo(function ServerGrid({
  servers,
  playbook,
  canaryStarted,
}: {
  servers: Record<ServerId, ServerView>;
  playbook: DemoPlaybook;
  canaryStarted: boolean;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-medium">Servers</p>
        <p className="text-muted-foreground text-xs">4 selected · simulated</p>
      </div>

      {/* One per row on a phone, with the badge beside the name: two across, "Ubuntu-Worker-03"
          does not fit. From sm up, two across with the badge on a line of its own. */}
      <ul className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {SERVERS.map((server) => {
          const view = servers[server.id];
          const badge = SERVER_BADGE[view.state];
          const tone = diskTone(view.disk);
          const canary = canaryStarted && server.id === CANARY;

          return (
            <li
              key={server.id}
              className={cn(
                'grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 rounded-lg border p-2.5 transition-colors duration-300 sm:grid-cols-1',
                view.state === 'canary' ? 'border-warning/40' : view.state === 'success' ? 'border-success/30' : 'border-border/60',
              )}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className={cn('size-1.5 shrink-0 rounded-full transition-colors duration-500', tone.dot)} title={tone.label} aria-hidden />
                  <span className="truncate text-[12.5px] font-medium">{server.name}</span>
                  {canary && (
                    <>
                      <Flag className="text-warning size-3 shrink-0" aria-hidden />
                      <span className="sr-only">(the canary server)</span>
                    </>
                  )}
                </div>
                <p className="text-muted-foreground mt-0.5 truncate text-[11px]">
                  {server.os} · {server.packageManager}
                </p>
              </div>

              <span
                className={cn(
                  'inline-flex items-center gap-1 justify-self-end rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors duration-300 sm:mt-2 sm:justify-self-start',
                  badge.className,
                )}
              >
                {badge.spin === true ? (
                  <Loader2 className="size-3 animate-spin" aria-hidden />
                ) : view.state === 'success' ? (
                  <Check className="size-3" aria-hidden />
                ) : null}
                {view.state === 'working' ? playbook.doing : badge.label}
              </span>

              <div className="col-span-2 mt-2 flex items-center gap-1.5 sm:col-span-1" title={`Disk ${view.disk}% used`}>
                <HardDrive className="text-muted-foreground/70 size-3 shrink-0" aria-hidden />
                <span className="bg-muted/60 h-1 flex-1 overflow-hidden rounded-full">
                  <span
                    className={cn('block h-full rounded-full transition-all duration-700', tone.bar)}
                    style={{ width: `${view.disk}%` }}
                  />
                </span>
                <span className="text-muted-foreground text-[11px] tabular-nums">
                  <span className="sr-only">Disk </span>
                  {view.disk}%<span className="sr-only"> used, {tone.label.toLowerCase()}</span>
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
});

function RunButton({ phase, onStart }: { phase: Phase; onStart: () => void }) {
  const finished = phase === 'done' || phase === 'stopped';
  const content: { icon: LucideIcon; label: string; spin?: boolean } =
    phase === 'running'
      ? { icon: Loader2, label: 'Running…', spin: true }
      : phase === 'waiting'
        ? { icon: Hand, label: 'Waiting for Your Confirmation' }
        : finished
          ? { icon: RotateCcw, label: 'Run Again' }
          : { icon: ShieldCheck, label: 'Run Playbook Safely' };

  return (
    <div>
      <button
        type="button"
        onClick={onStart}
        disabled={phase === 'running' || phase === 'waiting'}
        className="bg-primary text-primary-foreground shadow-primary/25 inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg px-5 text-[15px] font-medium shadow-lg transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none"
      >
        <content.icon className={cn('size-4', content.spin === true && 'animate-spin')} aria-hidden />
        {content.label}
      </button>
      <p className="text-muted-foreground mt-2 text-center text-xs">No account needed. Nothing is sent anywhere.</p>
    </div>
  );
}

// ── The pipeline ────────────────────────────────────────────────────────────

const STAGES: { id: Stage; step: string; title: string; short: string; icon: LucideIcon; checks: string[] }[] = [
  {
    id: 'preflight',
    step: 'Stage 1',
    title: 'Preflight Checks',
    short: 'Preflight',
    icon: ShieldCheck,
    checks: ['SSH validation', 'OS detection', 'Package manager validation'],
  },
  {
    id: 'canary',
    step: 'Stage 2',
    title: 'Canary Server',
    short: 'Canary',
    icon: UserCheck,
    checks: ['First server execution', 'Validation', 'Approval checkpoint'],
  },
  {
    id: 'rollout',
    step: 'Stage 3',
    title: 'Fleet Rollout',
    short: 'Rollout',
    icon: Layers,
    checks: ['Batch 1 · 2 servers', 'Batch 2 · 1 server', 'Completion'],
  },
];

const STAGE_LOOK: Record<StageState, { frame: string; pill: string; label: string; short: string }> = {
  pending: { frame: 'border-border/60', pill: 'text-muted-foreground', label: 'Pending', short: 'Pending' },
  running: { frame: 'border-primary/40 bg-primary/[0.04]', pill: 'text-primary', label: 'Running', short: 'Running' },
  waiting: { frame: 'border-warning/40 bg-warning/[0.05]', pill: 'text-warning', label: 'Waiting for you', short: 'Waiting' },
  success: { frame: 'border-success/30 bg-success/[0.04]', pill: 'text-success', label: 'Success', short: 'Success' },
  skipped: { frame: 'border-border/60 bg-muted/20', pill: 'text-muted-foreground', label: 'Not run', short: 'Not run' },
};

function StageIcon({ state }: { state: StageState }) {
  switch (state) {
    case 'running':
      return <Loader2 className="size-3 animate-spin" aria-hidden />;
    case 'success':
      return <Check className="size-3" aria-hidden />;
    case 'waiting':
      return <Hand className="size-3" aria-hidden />;
    case 'pending':
    case 'skipped':
      return null;
  }
}

function CheckMark({ state }: { state: CheckState }) {
  switch (state) {
    case 'running':
      return <Loader2 className="text-primary size-3.5 shrink-0 animate-spin" aria-hidden />;
    case 'success':
      return <Check className="text-success size-3.5 shrink-0" aria-hidden />;
    case 'skipped':
      return <Minus className="text-muted-foreground size-3.5 shrink-0" aria-hidden />;
    case 'pending':
      return <Circle className="text-muted-foreground/40 size-3.5 shrink-0" aria-hidden />;
  }
}

const Pipeline = memo(function Pipeline({
  stages,
  checks,
  progress,
}: {
  stages: Record<Stage, StageState>;
  checks: Record<Stage, CheckState[]>;
  progress: number;
}) {
  return (
    // On a phone, three compact steps across: the console, not the check lists, carries the detail
    // there, and three stacked cards pushed it half a screen further down.
    <ol className="grid grid-cols-3 gap-2 sm:gap-3" aria-label="Rollout stages">
      {STAGES.map((stage) => {
        const state = stages[stage.id];
        const look = STAGE_LOOK[state];

        return (
          <li key={stage.id} className={cn('rounded-xl border p-2.5 transition-colors duration-300 sm:p-3.5', look.frame)}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground text-[10px] font-semibold tracking-[0.14em] uppercase sm:text-[10.5px]">
                {stage.step}
              </span>
              <span className={cn('hidden items-center gap-1 text-[11px] font-medium sm:inline-flex', look.pill)}>
                <StageIcon state={state} />
                {look.label}
              </span>
            </div>

            <p className="mt-1 flex items-center gap-2 text-[13px] font-medium sm:mt-1.5 sm:text-sm">
              <stage.icon
                className={cn('hidden size-4 shrink-0 transition-colors sm:block', state === 'pending' ? 'text-muted-foreground' : look.pill)}
                aria-hidden
              />
              <span className="sm:hidden">{stage.short}</span>
              <span className="hidden sm:inline">{stage.title}</span>
            </p>
            <p className={cn('mt-1 inline-flex items-center gap-1 text-[11px] font-medium sm:hidden', look.pill)}>
              <StageIcon state={state} />
              {look.short}
            </p>

            <ul className="mt-3 hidden space-y-1.5 sm:block">
              {stage.checks.map((label, index) => {
                const check = checks[stage.id][index];

                return (
                  <li key={label} className="flex items-start gap-2 text-xs leading-snug">
                    <span className="mt-px">
                      <CheckMark state={check} />
                    </span>
                    <span className={check === 'pending' || check === 'skipped' ? 'text-muted-foreground' : 'text-foreground/90'}>
                      {label}
                    </span>
                  </li>
                );
              })}
            </ul>

            {stage.id === 'rollout' && (
              <div className="mt-2 sm:mt-3">
                <div className="flex items-baseline justify-between text-[11px]">
                  <span className="text-muted-foreground hidden sm:inline">Fleet progress</span>
                  <span className="font-medium tabular-nums">{progress}%</span>
                </div>
                <div
                  className="bg-muted/60 mt-1.5 h-1.5 overflow-hidden rounded-full"
                  role="progressbar"
                  aria-label="Fleet progress"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={progress}
                >
                  <div
                    className={cn('h-full rounded-full transition-all duration-700', progress === 100 ? 'bg-success' : 'bg-primary')}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
});

// ── The console ─────────────────────────────────────────────────────────────

const TAG: Record<LogStage, { label: string; className: string }> = {
  run: { label: 'Run', className: 'text-slate-400' },
  preflight: { label: 'Preflight', className: 'text-sky-300' },
  canary: { label: 'Canary', className: 'text-amber-300' },
  rollout: { label: 'Rollout', className: 'text-violet-300' },
  done: { label: 'Done', className: 'text-emerald-300' },
};

const TONE: Record<Tone, string> = {
  plain: 'text-slate-200',
  ok: 'text-emerald-300',
  wait: 'text-amber-200',
  note: 'text-slate-400',
};

const ConsoleLine = memo(function ConsoleLine({ line, text, typing }: { line: Line; text: string; typing: boolean }) {
  const tag = TAG[line.stage];

  return (
    <li className="grid grid-cols-[4.25rem_minmax(0,1fr)] gap-x-2 sm:grid-cols-[1.75rem_4.75rem_minmax(0,1fr)] sm:gap-x-3">
      <span className="hidden text-right text-slate-600 tabular-nums select-none sm:block">{line.id}</span>
      <span className={cn('select-none', tag.className)}>{tag.label}</span>
      <span className={cn('break-words', TONE[line.tone])}>
        {text}
        {typing && (
          <span className="ml-px inline-block h-[1.1em] w-[2px] translate-y-[3px] animate-pulse bg-sky-300" aria-hidden />
        )}
      </span>
    </li>
  );
});

const CONSOLE_STATUS: Record<Phase, { label: string; className: string; dot: string }> = {
  idle: { label: 'Not started', className: 'text-slate-500', dot: 'bg-slate-600' },
  running: { label: 'Live', className: 'text-sky-300', dot: 'bg-sky-400 animate-pulse' },
  waiting: { label: 'Waiting for you', className: 'text-amber-200', dot: 'bg-amber-400' },
  done: { label: 'Finished', className: 'text-emerald-300', dot: 'bg-emerald-400' },
  stopped: { label: 'Stopped by you', className: 'text-slate-300', dot: 'bg-slate-400' },
};

function Console({
  lines,
  typing,
  phase,
  playbookName,
}: {
  lines: Line[];
  typing: DemoState['typing'];
  phase: Phase;
  playbookName: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  // Follow new output unless the visitor has scrolled up to read something.
  const follow = useRef(true);
  const status = CONSOLE_STATUS[phase];

  useEffect(() => {
    if (lines.length === 0) {
      follow.current = true;
    }

    const element = box.current;

    // The console scrolls itself; the page never moves for it.
    if (element !== null && follow.current) {
      element.scrollTop = element.scrollHeight;
    }
  }, [lines.length, typing?.shown]);

  return (
    <div className="flex min-h-[20rem] flex-1 flex-col overflow-hidden rounded-xl bg-[#0a0f1c] ring-1 ring-white/[0.06] sm:min-h-[22rem]">
      <div className="flex items-center justify-between gap-3 border-b border-white/[0.06] px-4 py-2.5">
        <p className="flex min-w-0 items-center gap-2 text-xs">
          <ScrollText className="size-3.5 shrink-0 text-slate-400" aria-hidden />
          <span className="font-medium text-slate-200">Run output</span>
          <span className="hidden truncate text-slate-500 sm:inline">· {playbookName}</span>
        </p>
        <p className={cn('flex shrink-0 items-center gap-1.5 text-xs font-medium', status.className)}>
          <span className={cn('size-1.5 rounded-full', status.dot)} aria-hidden />
          {status.label}
        </p>
      </div>

      <div
        ref={box}
        onScroll={(event) => {
          const element = event.currentTarget;
          follow.current = element.scrollHeight - element.scrollTop - element.clientHeight < 32;
        }}
        className="min-h-0 flex-1 basis-0 overflow-y-auto px-4 py-3"
        role="region"
        aria-label="Run output"
        tabIndex={0}
      >
        {lines.length === 0 && typing === null ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-4 text-center">
            <span className="flex size-10 items-center justify-center rounded-full bg-white/[0.04] ring-1 ring-white/[0.08]">
              <Play className="size-4 translate-x-px text-slate-300" aria-hidden />
            </span>
            <p className="text-sm font-medium text-slate-200">Select a playbook and run a safe simulation.</p>
            <p className="max-w-xs text-xs leading-relaxed text-slate-500">
              Four example servers. Everything below is played in your browser.
            </p>
          </div>
        ) : (
          <ol className="space-y-1 font-mono text-[11.5px] leading-5 sm:text-[12.5px] sm:leading-6">
            {lines.map((line) => (
              <ConsoleLine key={line.id} line={line} text={line.text} typing={false} />
            ))}
            {typing !== null && (
              <ConsoleLine
                line={{ ...typing.line, id: lines.length + 1 }}
                text={typing.line.text.slice(0, typing.shown)}
                typing
              />
            )}
          </ol>
        )}
      </div>
    </div>
  );
}

// ── The confirmation, and how it ended ──────────────────────────────────────

function Checkpoint({
  anchor,
  onStop,
  onContinue,
}: {
  anchor: RefObject<HTMLDivElement>;
  onStop: () => void;
  onContinue: () => void;
}) {
  const canary = SERVERS.find((server) => server.id === CANARY)?.name ?? '';

  return (
    <div
      ref={anchor}
      className="border-warning/40 bg-warning/[0.07] animate-fade-in-up flex scroll-mt-24 scroll-mb-6 flex-col gap-4 rounded-xl border p-4 sm:flex-row sm:items-center"
    >
      <Hand className="text-warning hidden size-5 shrink-0 sm:block" aria-hidden />
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-medium">The canary server, {canary}, succeeded.</p>
        <p className="text-muted-foreground mt-0.5">
          Check its output above. Nothing else starts until you continue: 3 servers waiting.
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        <button
          type="button"
          onClick={onStop}
          className="border-border/60 hover:bg-muted/50 inline-flex h-10 items-center justify-center rounded-lg border px-4 text-sm font-medium transition-colors"
        >
          Stop Here
        </button>
        <button
          type="button"
          onClick={onContinue}
          className="bg-primary text-primary-foreground inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium transition-opacity hover:opacity-90"
        >
          <Check className="size-4" aria-hidden />
          Continue on 3 Servers
        </button>
      </div>
    </div>
  );
}

function Outcome({
  anchor,
  stopped,
  playbook,
  onAnother,
}: {
  anchor: RefObject<HTMLDivElement>;
  stopped: boolean;
  playbook: DemoPlaybook;
  onAnother: () => void;
}) {
  const stats = stopped
    ? [
        { value: '4', label: 'Servers Checked' },
        { value: '1', label: `Server ${playbook.done}` },
        { value: '3', label: 'Not Touched' },
        { value: '0', label: 'Failures' },
      ]
    : [
        { value: '4', label: 'Servers Checked' },
        { value: '4', label: `Servers ${playbook.done}` },
        { value: '0', label: 'Failures' },
        { value: null, label: 'Safe Canary Rollout Completed' },
      ];

  return (
    <div
      ref={anchor}
      className={cn(
        'animate-fade-in-up scroll-mt-24 scroll-mb-6 rounded-xl border p-4 sm:p-5',
        stopped ? 'border-border/60 bg-muted/20' : 'border-success/30 bg-success/[0.06]',
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-full',
            stopped ? 'bg-muted text-foreground' : 'bg-success text-success-foreground',
          )}
        >
          {stopped ? <Hand className="size-4" aria-hidden /> : <Check className="size-5" aria-hidden />}
        </span>
        <div className="min-w-0">
          <p className="font-semibold">{stopped ? 'Stopped After the Canary' : 'Automation Completed Successfully'}</p>
          <p className="text-muted-foreground mt-0.5 text-sm">
            {stopped
              ? 'You stopped the run at the confirmation. The other three servers were never started.'
              : `${playbook.name} ran on all four servers, one first and the rest after you confirmed.`}
          </p>
        </div>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="border-border/60 bg-card flex flex-col rounded-lg border px-3 py-2.5">
            <dt className="text-muted-foreground order-2 mt-0.5 text-xs leading-snug">{stat.label}</dt>
            <dd className="order-1 flex h-7 items-center text-xl font-semibold tabular-nums">
              {stat.value ?? (
                <>
                  <Check className="text-success size-6" strokeWidth={2.5} aria-hidden />
                  <span className="sr-only">Yes</span>
                </>
              )}
            </dd>
          </div>
        ))}
      </dl>

      {!stopped && <p className="text-muted-foreground mt-4 text-sm leading-relaxed">{playbook.note}</p>}

      <div className="border-border/60 mt-5 flex flex-col gap-4 border-t pt-5 xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-0">
          <p className="font-semibold">Automate Your Real Infrastructure</p>
          <p className="text-muted-foreground mt-0.5 text-sm">
            {TRIAL.days}-day free trial, no card. Connect your own server over SSH and run the same playbook.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Link
            to="/register"
            className="bg-primary text-primary-foreground inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium transition-opacity hover:opacity-90"
          >
            Get Started Free
            <ArrowRight className="size-4" aria-hidden />
          </Link>
          <button
            type="button"
            onClick={onAnother}
            className="border-border/60 hover:bg-muted/50 inline-flex h-10 items-center justify-center gap-2 rounded-lg border px-4 text-sm font-medium transition-colors"
          >
            <RotateCcw className="size-4" aria-hidden />
            Run Another Playbook
          </button>
        </div>
      </div>
    </div>
  );
}

// ── What the run proved ─────────────────────────────────────────────────────

const ProofStrip = memo(function ProofStrip({ proofs }: { proofs: Proof[] }) {
  return (
    <ul className="mt-8 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" aria-label="What the run shows">
      {PROOFS.map((proof) => {
        const shown = proofs.includes(proof.id);

        return (
          <li
            key={proof.id}
            className={cn(
              'flex items-center gap-2 rounded-lg border px-3 py-2.5 text-xs font-medium transition-colors duration-500',
              shown ? 'border-success/30 bg-success/[0.06] text-foreground' : 'border-border/60 text-muted-foreground',
            )}
          >
            <span
              className={cn(
                'flex size-4 shrink-0 items-center justify-center rounded-full transition-colors duration-500',
                shown ? 'bg-success text-success-foreground' : 'bg-muted text-muted-foreground',
              )}
            >
              <Check className="size-2.5" strokeWidth={3} aria-hidden />
            </span>
            <span className="leading-snug">{proof.label}</span>
          </li>
        );
      })}
    </ul>
  );
});
